import { useEffect, useId, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from "react";
import { createPortal } from "react-dom";

export const MAX_FILTER_OPTIONS = 100;
export const FILTER_SEARCH_DEBOUNCE_MS = 150;
const POPOVER_WIDTH = 288;

export type FilterChoice = {
  readonly key: string;
  readonly label: string;
  readonly count: string;
  readonly checked: boolean;
  readonly onToggle: () => void;
};

export type FilterChip = {
  readonly key: string;
  readonly column: string;
  readonly label: string;
  readonly onRemove: () => void;
};

function FilterIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 5h18l-7 8v6l-4 2v-8z" />
    </svg>
  );
}

function tabbable(node: HTMLElement): HTMLElement[] {
  return [...node.querySelectorAll<HTMLElement>("input, button")].filter((item) => !item.hasAttribute("disabled"));
}

export function ColumnFilter(props: { label: string; choices: () => readonly FilterChoice[]; selected: number }) {
  const popoverId = useId();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [place, setPlace] = useState({ top: 0, left: 0 });
  const [text, setText] = useState("");
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (text === query) return;
    const timer = setTimeout(() => setQuery(text), FILTER_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [text, query]);

  useEffect(() => {
    if (!open) return;
    popoverRef.current?.querySelector<HTMLInputElement>("input[type='search']")?.focus();
    function onPointerDown(event: PointerEvent) {
      const target = event.target;
      if (target instanceof Node && (popoverRef.current?.contains(target) || buttonRef.current?.contains(target))) return;
      close(false);
    }
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [open]);

  function close(refocus: boolean) {
    setOpen(false);
    setText("");
    setQuery("");
    if (refocus) buttonRef.current?.focus();
  }

  function toggle() {
    if (open) {
      close(false);
      return;
    }
    const rect = buttonRef.current?.getBoundingClientRect();
    const right = window.innerWidth - POPOVER_WIDTH - 8;
    setPlace({ top: (rect?.bottom ?? 0) + 4, left: Math.max(8, Math.min(rect?.left ?? 0, right)) });
    setOpen(true);
  }

  function onKeyDown(event: ReactKeyboardEvent<HTMLDivElement>) {
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      close(true);
      return;
    }
    if (event.key !== "Tab" || !popoverRef.current) return;
    const items = tabbable(popoverRef.current);
    const first = items[0];
    const last = items[items.length - 1];
    if (!first || !last) return;
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  const all = open ? props.choices() : [];
  const needle = query.trim().toLowerCase();
  const matching = needle.length === 0 ? all : all.filter((choice) => choice.label.toLowerCase().includes(needle));
  const shown = matching.slice(0, MAX_FILTER_OPTIONS);
  const name = props.selected > 0 ? `Filter ${props.label}, ${props.selected} selected` : `Filter ${props.label}`;

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className={props.selected > 0 ? "column-filter column-filter-active" : "column-filter"}
        aria-label={name}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={popoverId}
        onClick={toggle}
      >
        <FilterIcon />
        {props.selected > 0 ? (
          <span className="column-filter-count" aria-hidden="true">
            {props.selected}
          </span>
        ) : null}
      </button>
      {createPortal(
        <div
          ref={popoverRef}
          id={popoverId}
          className="column-filter-popover"
          role="dialog"
          aria-label={`Filter ${props.label}`}
          hidden={!open}
          style={{ top: place.top, left: place.left }}
          onKeyDown={onKeyDown}
        >
          {open ? (
            <>
              <input
                type="search"
                aria-label={`Search ${props.label} values`}
                placeholder="Search"
                value={text}
                onChange={(event) => setText(event.target.value)}
              />
              {shown.length === 0 ? <p className="column-filter-empty">No values</p> : null}
              <ul className="column-filter-options" aria-label={`${props.label} values`}>
                {shown.map((choice, index) => (
                  <li key={choice.key}>
                    <label>
                      <input
                        type="checkbox"
                        checked={choice.checked}
                        aria-describedby={`${popoverId}-count-${index}`}
                        onChange={choice.onToggle}
                      />
                      <span className="column-filter-label">{choice.label}</span>
                    </label>
                    <span id={`${popoverId}-count-${index}`} className="column-filter-option-count">
                      {choice.count}
                    </span>
                  </li>
                ))}
              </ul>
              {matching.length > shown.length ? (
                <p className="column-filter-more">{`Showing ${shown.length} of ${matching.length} values. Search to narrow the list.`}</p>
              ) : null}
            </>
          ) : null}
        </div>,
        document.body,
      )}
    </>
  );
}

export function FilterChips(props: { chips: readonly FilterChip[]; onClearAll: () => void }) {
  if (props.chips.length === 0) return null;
  return (
    <div className="filter-chips" role="group" aria-label="Active filters">
      <ul>
        {props.chips.map((chip) => (
          <li key={chip.key} className="filter-chip">
            <span>{`${chip.column}: ${chip.label}`}</span>
            <button type="button" aria-label={`Remove ${chip.column}: ${chip.label}`} onClick={chip.onRemove}>
              <span aria-hidden="true">×</span>
            </button>
          </li>
        ))}
      </ul>
      <button type="button" className="filter-clear" onClick={props.onClearAll}>
        Clear all
      </button>
    </div>
  );
}
